import { ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/theme/useTheme';
import { Code, Section } from './Section';
import { BoardPatternTile } from './BoardPatternTile';
import { WideTablePatternTile } from './WideTablePatternTile';
import { TimeGridPatternTile } from './TimeGridPatternTile';
const examples = [
  {
    id: 'files',
    title: 'Файлы и вложения',
    detail:
      'Перетаскивание, вставка скриншота, шаги добавления, ошибка с повтором. Картинки и PDF — в панели своего окна.',
    api: 'FileDropzone · FilePasteArea · FileChip · FilePreview',
  },
  {
    id: 'board',
    title: 'Доска по статусам',
    detail:
      'Число и суммы в колонках, срок и вложения на карточке. Перенос мышью и клавиатурой, действие с отменой.',
    api: 'KanbanBoard · Money · Deadline · Toast',
  },
  {
    id: 'table',
    title: 'Широкая таблица',
    detail:
      'Горизонтальная прокрутка, закреплённые первая колонка, шапка и итоги. Следующие колонки занимают место заглушками.',
    api: 'WideTable · Table stickyFirst · TableFooter',
  },
  {
    id: 'schedule',
    title: 'Сетка времени',
    detail:
      'День, неделя и месяц. Смены, линия «сегодня», закреплённые подписи строк и следующие периоды при прокрутке.',
    api: 'TimeGrid · buildTimeAxis · packShifts',
  },
];
export function OperationsPatternsSection() {
  const { mode } = useTheme();
  useEffect(() => {
    if (window.location.hash !== '#operation-patterns') return;
    const section = document.getElementById('operation-patterns');
    if (!section) return;
    const root = section.parentElement?.parentElement;
    const main = section.parentElement;
    if (!root || !main) return;
    let active = true;
    // Поздние шрифты и измерения прежних плиток ещё меняют высоту галереи.
    // Держим начальную цель до первого действия человека, затем прокрутка только его.
    const align = () =>
      requestAnimationFrame(() => {
        if (!active || window.location.hash !== '#operation-patterns') return;
        const header = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
        root.scrollTo({
          top:
            root.scrollTop +
            section.getBoundingClientRect().top -
            root.getBoundingClientRect().top -
            header -
            24,
          behavior: 'instant',
        });
      });
    const observer = new ResizeObserver(align);
    const stop = () => {
      active = false;
      observer.disconnect();
      root.removeEventListener('scroll', align);
      for (const event of ['wheel', 'pointerdown', 'keydown']) root.removeEventListener(event, stop, true);
    };
    observer.observe(main);
    root.addEventListener('scroll', align, { passive: true });
    for (const event of ['wheel', 'pointerdown', 'keydown']) root.addEventListener(event, stop, true);
    align();
    void document.fonts.ready.then(align);
    return stop;
  }, []);
  return (
    <Section
      id="operation-patterns"
      title="Рабочие процессы"
      lead="Файлы и вложения для любых сервисов. Первая часть волны 2: на существующей оболочке, с примерными данными."
    >
      {examples
        .filter((example) => example.id === 'files')
        .map((example) => (
          <div key={example.id} className="overflow-hidden rounded-lg border border-line bg-card">
            <div className="flex flex-wrap items-center gap-4 border-b border-line p-5">
              <div className="min-w-0 flex-1">
                <h3 className="type-heading">{example.title}</h3>
                <p className="mt-1 text-sm text-fg-muted">{example.detail}</p>
              </div>
              <Link
                to={'/design/operations?demo=' + example.id}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                Открыть образец
                <ArrowRight className="size-4" />
              </Link>
            </div>
            <iframe
              key={mode}
              loading="lazy"
              title={example.title + ' — рабочий образец'}
              src={'/design/operations?embedded&demo=' + example.id}
              className="h-96 w-full border-0"
            />
            <p className="border-t border-line p-4">
              <Code>{example.api}</Code>
            </p>
          </div>
        ))}
      <BoardPatternTile />
      <WideTablePatternTile />
      <TimeGridPatternTile />
    </Section>
  );
}
